import { insightsIntl } from "../../strings/intl";
import type { ReferencedChartTabProps, ReferencedMultiChartDataPoint, ValueType } from "@wayflyer/flyui-dataviz";
import { type IntlShape, useIntl } from "react-intl";
import * as React from "react";
import { useCallback } from "react";
// eslint-disable-next-line no-restricted-imports
import moment, { type Moment } from "moment";
import { useLoaderData } from "react-router";
import { FullScreenLoader } from "shared-ui";

import { Dimensions, MetricTypes } from "../../autogen/metrics";
import { metricDescription, metricTooltip } from "../../strings/metric-messages";
import type { InsightsLoaderReturn } from "../../loader";
import { formatValue } from "../../strings/type-formatting";
import { getUrls } from "../../urls";
import {
  allHistoricalData,
  xDomainSteps,
  xFormatDate,
  yoyReferenceForTimeRange,
} from "../../utils/custom-time-range-helpers";
import { dateRangePresets, useDateAndTemp } from "../../utils/date";
import { type DashboardOutput, getDashboardOutput } from "../../utils/fetch-api";
import fetchDashboardData from "../../utils/fetch-dashboard-data";
import fetchDashboardMetaData, { type DashboardMetadata, getEmptyMetadata } from "../../utils/fetch-dashboard-metadata";
import { type CustomTimeRange, type MetricType, type TemporalResolution, TemporalResolutions } from "../../utils/types";

import { InternalTikTokSpendScreen } from "./internal-tiktok-spend-screen";
import { aggregateTikTokSummaryData } from "./tiktok-spend-datapoints";

const getInputMetrics = () => {
  return [
    {
      title: metricDescription(insightsIntl, MetricTypes.TIKTOK_AD_SPEND),
      metricType: MetricTypes.TIKTOK_AD_SPEND,
    },
  ];
};

const getTableTabs = (intl: IntlShape) => {
  return [
    {
      title: metricDescription(insightsIntl, MetricTypes.TIKTOK_AD_SPEND),
      tooltip: metricTooltip(intl, MetricTypes.TIKTOK_AD_SPEND),
      metricType: MetricTypes.TIKTOK_AD_SPEND,
    },
  ];
};

const getSummaryChartTabs = (intl: IntlShape) => {
  return [
    {
      title: metricDescription(insightsIntl, MetricTypes.TIKTOK_AD_SPEND),
      tooltip: metricTooltip(intl, MetricTypes.TIKTOK_AD_SPEND),
      metricType: MetricTypes.TIKTOK_AD_SPEND,
    },
  ];
};

export type TikTokAccountData = {
  [key: string]: {
    [MetricTypes.TIKTOK_ACCOUNT_NAME]: string;
    [MetricTypes.TIKTOK_WAYFLYER_BC_ID]: string | null;
  };
};

/**
 * Entry point
 */
const InternalTikTokAdSpend = () => {
  const intl = useIntl();
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath } = getUrls(customerDomain);
  const [chartsData, setChartsData] = React.useState<ReferencedMultiChartDataPoint[]>([]);

  const [accountsData, setAccountsData] = React.useState<TikTokAccountData | null>(null);
  const [rawChartData, setRawChartData] = React.useState<DashboardOutput | null>(null);
  const [rawCurrentData, setRawCurrentData] = React.useState<DashboardOutput | null>(null);
  const [rawReferenceData, setRawReferenceData] = React.useState<DashboardOutput | null>(null);

  const [requiresDataComputation, setRequiresDataComputation] = React.useState(false);
  const [currency, setCurrency] = React.useState("USD");

  const [groups, setGroups] = React.useState<string[]>([]);
  const [activeGroups, setActiveGroups] = React.useState<string[]>([]);
  const [initialActiveGroups, setInitialActiveGroups] = React.useState(true);
  const [groupsMapping, setGroupsMapping] = React.useState<Record<string, number>>({});

  const [requiresDataFetch, setRequiresDataFetch] = React.useState(true);
  const [isLoadingRawData, setIsLoadingRawData] = React.useState(false);

  const [selectedCurrentTimeRangeEnabled, setSelectedCurrentTimeRangeEnabled] = React.useState(true);
  const [selectedReferenceTimeRangeEnabled, setSelectedReferenceTimeRangeEnabled] = React.useState(false);

  const [customerMetadata, setCustomerMetadata] = React.useState<DashboardMetadata>(getEmptyMetadata());
  const [summaryData, setSummaryData] = React.useState<ReferencedChartTabProps[]>(getSummaryChartTabs(intl));

  const allRawData = React.useMemo(
    () => [customerMetadata, rawChartData, rawCurrentData, rawReferenceData, accountsData],
    [customerMetadata, rawChartData, rawCurrentData, rawReferenceData, accountsData]
  );

  const formatChartValue = (value: ValueType) => {
    const metricType = { type: "CURRENCY", currencyCode: currency } as MetricType;
    return formatValue({ value: value.toString(), metricType });
  };

  React.useEffect(() => {
    if (allRawData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoadingRawData(false);
    }
  }, [allRawData]);

  const onChange = () => {
    setRequiresDataFetch(true);
  };

  const getInsightsDataPointsPerDay = useCallback(
    (day: Moment, temporalResolution: TemporalResolution, pointsByDay, metricType: MetricTypes, groups: string[]) => {
      const returnData = {};

      groups.forEach((group) => {
        // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
        const thisYearValue = getDataPoint(day, group + metricType, pointsByDay);
        let sameDayLastYear = day.clone().subtract(1, "year");
        if (temporalResolution.resolution === TemporalResolutions.WEEKLY.resolution) {
          sameDayLastYear = day.clone().subtract(1, "year").startOf("week");
        }
        const lastYearValue = getDataPoint(sameDayLastYear, group + metricType, pointsByDay);

        returnData[`${group}${metricType}Current`] = thisYearValue ? parseFloat(thisYearValue) : null;
        returnData[`${group}${metricType}Reference`] = lastYearValue ? parseFloat(lastYearValue) : null;
      });

      return returnData;
    },
    []
  );

  const {
    selectedTimeRange,
    handleTimeRangeChange,
    currentPreset,
    temporalResolution,
    selectedButtonIndex,
    hideWeekly,
    hideMonthly,
    handleTempResChange,
  } = useDateAndTemp(onChange, onChange);
  const getChartData = useCallback(
    (
      timeRange: CustomTimeRange,
      data: DashboardOutput,
      temporalResolution: TemporalResolution,
      inputTabs: ReferencedChartTabProps[],
      tableTabs: ReferencedChartTabProps[],
      groups: string[]
    ) => {
      const steps = xDomainSteps(timeRange.start, timeRange.end, temporalResolution);
      const dataByDay = {};
      data.data.map((row) => {
        dataByDay[moment(row["day"]).valueOf()] = row;
      });

      return steps.reduce(
        (multiLineData, step: Moment) => {
          const day = xFormatDate(step, temporalResolution, steps[0], steps[steps.length - 1]);
          const dayRaw = step.format("YYYY-MM-DD");
          multiLineData.chartData.push({
            day,
            dayRaw,
            ...inputTabs.reduce((linesData, tab) => {
              linesData = {
                ...linesData,
                ...getInsightsDataPointsPerDay(
                  step,
                  temporalResolution,
                  dataByDay,
                  tab.metricType as MetricTypes,
                  groups
                ),
              };
              return linesData;
            }, {}),
          });
          return multiLineData;
        },
        {
          chartData: [],
        } as { chartData: ReferencedMultiChartDataPoint[] }
      );
    },
    [getInsightsDataPointsPerDay]
  );

  const getDataPoint = (day: Moment, metricType: string, pointsByDay) => {
    return pointsByDay?.[day.valueOf()]?.[metricType];
  };

  const addTotalGroup = (output: DashboardOutput): DashboardOutput => {
    output.groups.push(["Total"]);

    output.data = output.data.map((dataPoint) => {
      dataPoint["Total" + MetricTypes.TIKTOK_AD_SPEND] = Object.keys(dataPoint).reduce((total, key) => {
        if (key !== "day") {
          return total + dataPoint[key];
        } else {
          return total;
        }
      }, 0);
      return dataPoint;
    });

    output.summary["Total" + MetricTypes.TIKTOK_AD_SPEND] = Object.keys(output.summary).reduce((total, key) => {
      if (key !== "day") {
        return total + output.summary[key];
      } else {
        return total;
      }
    }, 0);

    return output;
  };

  const fetchAccountData = React.useCallback(() => {
    fetchDashboardData(
      dashboardDataPath,
      "tiktok_insights_accounts_ctr",
      "TO_DATE",
      [],
      [],
      [],
      [],
      (data) => {
        const accounts = {};
        data.data.forEach(
          (account) =>
            (accounts[account[MetricTypes.TIKTOK_ACCOUNT_ID]] = {
              [MetricTypes.TIKTOK_ACCOUNT_NAME]: account[MetricTypes.TIKTOK_ACCOUNT_NAME],
              [MetricTypes.TIKTOK_WAYFLYER_BC_ID]: account[MetricTypes.TIKTOK_WAYFLYER_BC_ID],
            })
        );
        setAccountsData(accounts);
      },
      () => {
        setIsLoadingRawData(false);
      }
    );
  }, [dashboardDataPath]);

  const fetchCurrentSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [MetricTypes.TIKTOK_AD_SPEND],
          filename: "tiktok_insights_ctr",
          groupBys: [Dimensions.AD_ACCOUNT_ID],
        },
      ],
      selectedTimeRange,
      (data) => {
        setRawCurrentData(addTotalGroup(data));
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution]);

  const fetchReferenceSummaryData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [MetricTypes.TIKTOK_AD_SPEND],
          filename: "tiktok_insights_ctr",
          groupBys: [Dimensions.AD_ACCOUNT_ID],
        },
      ],
      yoyReferenceForTimeRange(selectedTimeRange, currentPreset),
      (data) => {
        setRawReferenceData(addTotalGroup(data));
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution, currentPreset]);

  const fetchChartData = React.useCallback(() => {
    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: [MetricTypes.TIKTOK_AD_SPEND],
          filename: "tiktok_insights_ctr",
          groupBys: [Dimensions.AD_ACCOUNT_ID],
        },
      ],
      allHistoricalData(selectedTimeRange),
      (data) => {
        const dataWithTotal = addTotalGroup(data);
        setRawChartData(dataWithTotal);
        setCurrency(dataWithTotal.currencyCode);
        setGroups(dataWithTotal.groups.map((group) => group[0]));

        if (initialActiveGroups) {
          setInitialActiveGroups(false);
          setActiveGroups(dataWithTotal.groups.map((group) => group[0]));

          const g = {};
          dataWithTotal.groups.forEach((group, index) => (g[group[0]] = index));
          setGroupsMapping(g);
        }
      },
      null,
      () => {
        setIsLoadingRawData(false);
      },
      temporalResolution
    );
  }, [dashboardDataPath, selectedTimeRange, temporalResolution, initialActiveGroups]);
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setIsLoadingRawData(true);

    fetchDashboardMetaData(
      dashboardDataPath,
      (data) => {
        setCustomerMetadata(data);
      },
      () => {}
    );
  }, [dashboardDataPath]);

  React.useEffect(() => {
    if (requiresDataFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setRawChartData(null);
      setRawCurrentData(null);
      setRawReferenceData(null);
      setAccountsData(null);

      setIsLoadingRawData(true);

      fetchChartData();
      fetchReferenceSummaryData();
      fetchCurrentSummaryData();
      fetchAccountData();

      setRequiresDataFetch(false);
      setRequiresDataComputation(true);
    }
  }, [requiresDataFetch, fetchChartData, fetchReferenceSummaryData, fetchCurrentSummaryData, fetchAccountData]);

  React.useEffect(() => {
    const computeRevenueData = () => {
      if (
        !requiresDataComputation ||
        requiresDataFetch ||
        !rawChartData ||
        !rawCurrentData ||
        !rawReferenceData ||
        !accountsData ||
        isLoadingRawData ||
        !groups.length
      ) {
        return;
      }

      const chartAndTableData = getChartData(
        selectedTimeRange,
        rawChartData,
        temporalResolution,
        getInputMetrics(),
        getTableTabs(intl),
        groups
      );

      setChartsData(chartAndTableData.chartData);

      setSummaryData(
        aggregateTikTokSummaryData(
          rawCurrentData,
          rawReferenceData,
          selectedTimeRange,
          getSummaryChartTabs(intl),
          accountsData,
          groups
        )
      );

      setRequiresDataComputation(false);
    };
    computeRevenueData();
  }, [
    getChartData,
    groups,
    isLoadingRawData,
    rawChartData,
    requiresDataComputation,
    requiresDataFetch,
    selectedTimeRange,
    temporalResolution,
    rawCurrentData,
    rawReferenceData,
    accountsData,
    intl,
  ]);

  const handleThisYearSwitch = (isOn: boolean) => {
    setSelectedCurrentTimeRangeEnabled(isOn);
  };

  const handleLastYearSwitch = (isOn: boolean) => {
    setSelectedReferenceTimeRangeEnabled(isOn);
  };

  const onSelectSummaryTab = (group: string, checked: boolean) => {
    if (checked && activeGroups.indexOf(group) < 0) {
      const newActive = groups.filter((c) => c === group || activeGroups.indexOf(c) > -1);
      setActiveGroups(newActive);
    } else {
      setActiveGroups(activeGroups.filter((c) => c !== group));
    }
  };

  if (!accountsData) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <InternalTikTokSpendScreen
        pageTitle="TikTok Ad Spend"
        chartData={chartsData}
        accountData={accountsData}
        groups={activeGroups}
        groupMapping={groupsMapping}
        formatValue={formatChartValue}
        datePickerHeaderProps={{
          currentPreset,
          startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
          endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
          dateRangePresets: dateRangePresets,
          onChange: handleTimeRangeChange,
        }}
        chartFilters={{
          switchOneLabel: insightsIntl.formatMessage({ id: "chart filter - This year", defaultMessage: "This year" }),
          switchTwoLabel: insightsIntl.formatMessage({ id: "chart filter - Last year", defaultMessage: "Last year" }),
          switchesDisabled: isLoadingRawData,
          hideWeekly,
          hideMonthly,
          onSwitchOne: handleThisYearSwitch,
          onSwitchTwo: handleLastYearSwitch,
          onClickDateRes: handleTempResChange,
          selectedButtonIndex: selectedButtonIndex,
        }}
        thisYearEnabled={selectedCurrentTimeRangeEnabled}
        lastYearEnabled={selectedReferenceTimeRangeEnabled}
        summaryData={summaryData}
        onSelectSummaryTab={onSelectSummaryTab}
      />
    </>
  );
};

export default InternalTikTokAdSpend;
